"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOrderToQuoteObject = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const dayjs = require("dayjs");
const pluralize_1 = require("../pluralize");
const expandItemProcessors_1 = require("./expandItemProcessors");
const expandOrderItems_1 = require("./expandOrderItems");
const getLatestOrderLineVersion_1 = require("./getLatestOrderLineVersion");
const getOrderLineConfigClientDescriptions_1 = require("./getOrderLineConfigClientDescriptions");
const getOrderLineConfigDescriptions_1 = require("./getOrderLineConfigDescriptions");
const getOrderLinesStartEndDate_1 = require("./getOrderLinesStartEndDate");
const getVariantCurrency_1 = require("./getVariantCurrency");
const getVariantFirstDate_1 = require("./getVariantFirstDate");
const getVariantMaxPaxByType_1 = require("./getVariantMaxPaxByType");
const getVariantNumberOfNights_1 = require("./getVariantNumberOfNights");
const getVariantNumberOfRounds_1 = require("./getVariantNumberOfRounds");
const getVariantPricingBands_1 = require("./getVariantPricingBands");
const getOrderLineTotalByType_1 = require("./getOrderLineTotalByType");
const currencyFormatter_1 = require("../currencyFormatter");
const itinerary_1 = require("../itinerary");
const _ = require("lodash");
const getCompulsoryDinnerDescriptions_1 = require("../hotel/getCompulsoryDinnerDescriptions");
const getHotelLineDescriptions_1 = require("./getHotelLineDescriptions");
const getOrderToQuoteObject = (order, order_variants_id, exchange_rates, include_data, show_pricing) => {
    let base_agent = {
        name: "Golfsavers",
        logo: `https://golfsavers.b-cdn.net/assets/images/golfsaver_email_logo.png?height=80&quality=10`,
        email: "support@golfsavers.com",
        phone: "+66 (0) 88 867 2867",
    };
    if (order.agent?.type !== "EXTERNAL") {
        base_agent = {
            name: order?.agent?.name || "Golfsavers",
            logo: `https://golfsavers.b-cdn.net${order?.agent?.config?.email?.logo
                ? JSON.parse(order?.agent?.config?.email?.logo)?.url
                : "/assets/images/golfsaver_email_logo.png?height=80&quality=10"}`,
            email: order?.agent?.config?.email?.contact_email || "support@golfsavers.com",
            phone: order?.agent?.config?.email?.contact_phone || "+66 (0) 88 867 2867",
        };
    }
    const response_obj = {
        agent: base_agent,
        order: {
            id: order.id,
        },
        quote: {
            link: order.source === "GOLFB2B"
                ? `https://www.golfb2b.com/orders/${order.id}`
                : `https://bookings.golfsavers.com/${order.id}`,
            show_variant_title: order_variants_id.length > 1,
            variants: order?.variants
                ?.filter((x) => order_variants_id.includes(x.id))
                .sort((a, b) => {
                // sort by the default variant first (order.default_variant_id)
                if (a.id === order.default_variant_id)
                    return -1;
                if (b.id === order.default_variant_id)
                    return 1;
                return 0;
            })
                .map((variant) => {
                const number_of_rounds = (0, getVariantNumberOfRounds_1.getVariantNumberOfRounds)(variant);
                const number_of_nights = (0, getVariantNumberOfNights_1.getVariantNumberOfNights)(variant);
                const first_date = (0, getVariantFirstDate_1.getVariantFirstDate)(variant);
                const last_date = dayjs(first_date)
                    .add(number_of_nights, "day")
                    .toISOString();
                const orderLines = variant?.order_lines?.filter((line) => {
                    if (!line.versions)
                        return false;
                    const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
                    if (!latest_version)
                        return false;
                    if (order.status &&
                        latest_version.status.includes("CANCELLED") &&
                        !order.status.includes("CANCELLED"))
                        return false;
                    return true;
                });
                const exchange_rate = exchange_rates.find((x) => x.currency === order?.display_currency &&
                    x.base_currency === (0, getVariantCurrency_1.getVariantCurrency)(variant));
                if (!orderLines)
                    return null;
                const expanded_items = (0, expandOrderItems_1.expandOrderItems)(orderLines, false);
                const lines = (0, expandItemProcessors_1.expandedItemProcessor)(expanded_items, order, include_data || false, exchange_rate);
                const grouped_legs = groupItemsIntoLegs(lines.order_lines);
                const latest_leg = _.last(variant.config?.legs);
                const per_person_pricing = (0, itinerary_1.getPerPersonPricingSummary)(latest_leg?.trip?.pga_pros || null, lines.order_lines, exchange_rate);
                if (!orderLines)
                    return null;
                return {
                    name: variant.name,
                    summary: `${number_of_nights} nights/${number_of_rounds} rounds`,
                    start_date: dayjs(first_date).format("ddd, Do MMM YYYY"),
                    end_date: dayjs(last_date).format("ddd, Do MMM YYYY"),
                    order_reference: order.reference,
                    non_golfers: (0, pluralize_1.pluralize)(Math.max(0, (0, getVariantMaxPaxByType_1.getVariantMaxPaxByType)(variant, "hotel") -
                        (0, getVariantMaxPaxByType_1.getVariantMaxPaxByType)(variant, "golf")), "non-golfer", "non-golfers"),
                    number_of_golfers: (0, pluralize_1.pluralize)((0, getVariantMaxPaxByType_1.getVariantMaxPaxByType)(variant, "golf"), "golfer", "golfers"),
                    regions: grouped_legs.legs.map((group) => {
                        const duration_dates = (0, getOrderLinesStartEndDate_1.getOrderLinesStartEndDate)(group.order_lines);
                        const first_date = duration_dates.start;
                        const last_date = duration_dates.end;
                        // get the first and last date
                        const golf_lines = group.order_lines.filter((line) => line.type === "GOLF");
                        const hotel_lines = group.order_lines.filter((line) => line.type === "HOTEL");
                        const transfer_lines = group.order_lines
                            .filter((line) => line.type === "TRANSFER")
                            .filter((line) => line.latest_version.config.vehicle.vehicle);
                        const excursion_lines = group.order_lines.filter((line) => line.type === "EXCURSION");
                        const custom_lines = group.order_lines.filter((line) => line.type === "CUSTOM");
                        // get the number of nights between the first and last date
                        const number_of_nights = dayjs(last_date).diff(dayjs(first_date), "days");
                        const hotel_line_summary = hotel_lines
                            .map((h) => {
                            const config = h.data;
                            return `${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_nights, "night", "nights")}, ${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_guests, "guest", "guests")}`;
                        })
                            .join(" / ");
                        const region_summary = () => {
                            if (hotel_lines.length > 0 && golf_lines.length > 0) {
                                return `${number_of_nights}N/${golf_lines.length}R`;
                            }
                            else if (hotel_lines.length > 0) {
                                return `${number_of_nights}N`;
                            }
                            else if (golf_lines.length > 0) {
                                return `${golf_lines.length}R`;
                            }
                        };
                        const region_summary_long = () => {
                            if (hotel_lines.length > 0 && golf_lines.length > 0) {
                                return `${number_of_nights} nights/${golf_lines.length} rounds`;
                            }
                            else if (hotel_lines.length > 0) {
                                return `${number_of_nights} nights`;
                            }
                            else if (golf_lines.length > 0) {
                                return `${golf_lines.length} rounds`;
                            }
                        };
                        return {
                            name: group.region.name.en,
                            start_date: dayjs(first_date).format("ddd, Do MMM YYYY"),
                            end_date: dayjs(last_date).format("ddd, Do MMM YYYY"),
                            summary: region_summary(),
                            summary_long: region_summary_long(),
                            golf: {
                                enabled: golf_lines.length > 0,
                                title: `Golf in ${group.region.name.en} (${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")})`,
                                summary: `${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")} of golf`,
                                tee_times: golf_lines.map((line) => {
                                    const config = line.latest_version.config;
                                    const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                                    const course = {
                                        id: config?.course?.id,
                                        latest_version: line.latest_version,
                                        name: config?.course?.name,
                                        date: date,
                                        tee_time: config?.tee_time,
                                        tee_time_range: {
                                            earliest: config?.green_fee?.earliest_time,
                                            latest: config?.green_fee?.latest_time,
                                        },
                                    };
                                    return course;
                                }),
                                rounds: golf_lines.map((line) => {
                                    const config = line.latest_version.config;
                                    const description = (0, getOrderLineConfigClientDescriptions_1.getOrderLineConfigClientDescriptions)(config, "GOLF");
                                    const line_total = (0, getOrderLineTotalByType_1.getOrderLineTotalByType)(config, "GOLF");
                                    const per_person_total = line_total / config?.where_when_who?.number_of_golfers;
                                    if (config?.green_fee?.odd_number_surcharge &&
                                        config?.where_when_who?.number_of_golfers % 2 !== 0) {
                                        description?.unshift(`Includes a surcharge of ${(0, currencyFormatter_1.currencyFormatter)(line?.currency, config?.green_fee?.odd_number_surcharge, false)} as one player will get their own cart`);
                                    }
                                    if (config?.green_fee?.rate_note) {
                                        description?.unshift(config?.green_fee?.rate_note);
                                    }
                                    if (description && config?.product?.name?.en)
                                        description.unshift(`${getGolfName(config)} ${show_pricing
                                            ? ` · ${(0, currencyFormatter_1.currencyFormatter)(line?.currency, per_person_total, false)} pp`
                                            : ""}`);
                                    const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                                    const course = {
                                        id: config?.course?.id,
                                        name: config?.course?.name,
                                    };
                                    return {
                                        title: `${date}: ${course?.name?.en}`,
                                        latest_version: line.latest_version,
                                        date: date,
                                        course: course,
                                        order_line: line,
                                        descriptions: description,
                                    };
                                }),
                                footnote: "",
                            },
                            hotels: {
                                enabled: hotel_lines.length > 0,
                                title: `Accommodation in 
                  ${group.region.name.en} (${hotel_line_summary}) `,
                                stays: hotel_lines.map((line) => {
                                    const config = line.latest_version.config;
                                    const dates = `${dayjs(config?.where_when_who?.check_in_date).format("ddd, Do MMM")} - ${dayjs(config?.where_when_who?.check_in_date)
                                        .add(config?.where_when_who?.number_of_nights, "day")
                                        .format("ddd, Do MMM")}`;
                                    const nights = (0, pluralize_1.pluralize)(config?.where_when_who?.number_of_nights, "night", "nights");
                                    const hotel = {
                                        id: config?.where_when_who?.hotel?.id,
                                        name: config?.where_when_who?.hotel?.name,
                                        star_rating: config?.where_when_who?.hotel?.star_rating,
                                        nights: nights,
                                    };
                                    return {
                                        title: `${dates}: ${hotel?.name?.en} ${hotel.star_rating && `(${hotel.star_rating}*)`}`,
                                        order_line: line,
                                        hotel: hotel,
                                        date: dates,
                                        descriptions: (0, getHotelLineDescriptions_1.getHotelLineDescriptions)(config),
                                        warnings: getHotelLineWarnings(config),
                                        dinners: (0, getCompulsoryDinnerDescriptions_1.getCompulsoryDinnerDescriptions)(config),
                                        check_in_out: `Check-in: ${config?.where_when_who?.hotel.check_in_time} / Check-out: ${config?.where_when_who?.hotel.check_out_time}`,
                                    };
                                }),
                            },
                            transfers: {
                                enabled: transfer_lines.length > 0,
                                title: `${transfer_lines.length > 1 ? "Transfers" : "Transfer"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(transfer_lines.length, "trip", "trips")})`,
                                description: `${(0, pluralize_1.pluralize)(transfer_lines.length, "trip", "trips")} in an air-conditioned, private vehicle`,
                                trips: convertTransfers(transfer_lines),
                            },
                            excursions: {
                                enabled: excursion_lines.length > 0,
                                title: excursion_lines.length === 0
                                    ? `Excursions in ${group.region.name.en}`
                                    : `${excursion_lines.length > 1
                                        ? "Excursions"
                                        : "Excursion"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(excursion_lines.length, "excursion", "excursions")})`,
                                description: `${(0, pluralize_1.pluralize)(excursion_lines.length, "excursion", "excursions")}`,
                                trips: excursion_lines.map((line) => {
                                    const config = line.latest_version
                                        .config;
                                    const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                                    const excursion = {
                                        id: config?.product?.excursion?.id,
                                        name: config?.product?.excursion?.name,
                                    };
                                    return {
                                        title: `${date}: ${excursion?.name?.en}`,
                                        date: date,
                                        order_line: line,
                                        excursion: excursion,
                                        descriptions: (0, getOrderLineConfigDescriptions_1.getOrderLineConfigDescriptions)(config, "EXCURSION"),
                                    };
                                }),
                            },
                            custom: {
                                enabled: custom_lines.length > 0,
                                title: `${custom_lines.length > 1 ? "Custom" : "Custom"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(custom_lines.length, "item", "items")})`,
                                description: `${(0, pluralize_1.pluralize)(custom_lines.length, "item", "items")}`,
                                items: custom_lines.map((line) => {
                                    const config = line.latest_version.config;
                                    return {
                                        title: config?.product?.name?.en,
                                        order_line: line,
                                        descriptions: (0, getOrderLineConfigDescriptions_1.getOrderLineConfigDescriptions)(config, "CUSTOM"),
                                    };
                                }),
                            },
                        };
                    }),
                    per_person_pricing: per_person_pricing,
                    pricing_bands: (0, getVariantPricingBands_1.getVariantPricingBands)(variant),
                };
            }),
        },
        client: order.client,
    };
    return response_obj;
};
exports.getOrderToQuoteObject = getOrderToQuoteObject;
function groupItemsIntoLegs(items) {
    // Sort items by start date to ensure proper ordering
    const sortedItems = [...items].sort((a, b) => {
        const dateComparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        if (dateComparison !== 0)
            return dateComparison;
        const typeOrder = ["TRANSFER", "GOLF", "EXCURSION", "HOTEL"];
        return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
    const legs = [];
    let currentLeg = null;
    sortedItems.forEach((item, index) => {
        // If this is the first item or we've changed regions
        if (!currentLeg || currentLeg.region.id !== item.region.id) {
            // Close out the previous leg if it exists
            if (currentLeg) {
                currentLeg.endDate = item.start_date;
            }
            // Start a new leg
            currentLeg = {
                region: item.region,
                startDate: item.start_date,
                endDate: "",
                order_lines: [],
            };
            legs.push(currentLeg);
        }
        // Add item to current leg
        currentLeg.order_lines.push(item);
        // If this is the last item, set the end date
        if (index === sortedItems.length - 1) {
            currentLeg.endDate = item.start_date;
        }
    });
    return { legs };
}
function getHotelLineWarnings(config) {
    const warnings = new Set();
    config.rooms.rooms.forEach((room) => {
        room.rate.forEach((rate) => {
            if (rate?.warnings) {
                rate.warnings.forEach((warning) => {
                    warnings.add(warning);
                });
            }
        });
    });
    return Array.from(warnings);
}
function convertTransfers(transfers) {
    const transfer_lines = [];
    const golf_transfers = transfers.filter((transfer) => transfer.latest_version.config.transfer_legs.outbound.to_type ===
        "Course" ||
        transfer.latest_version.config.transfer_legs.inbound.from_type ===
            "Course");
    const all_golf_same = checkMatchingTransfers(golf_transfers.map((t) => t.latest_version.config));
    transfers.forEach((transfer) => {
        const { config } = transfer.latest_version;
        const { outbound, inbound } = config.transfer_legs;
        const { vehicle } = config;
        if (!vehicle?.vehicle)
            return;
        const region = transfer?.region;
        const is_custom = config.where_when_who.type === "CUSTOM";
        let is_airport_transfer = false;
        if (outbound.to_type === "Airport" ||
            outbound.from_type === "Airport" ||
            inbound.to_type === "Airport" ||
            inbound.from_type === "Airport") {
            is_airport_transfer = true;
        }
        if ((outbound.to_type === "Area" ||
            outbound.from_type === "Area" ||
            outbound.to_type === "Hotel" ||
            outbound.from_type === "Hotel") &&
            outbound.to_type !== "Course") {
            // Hotel transfer
            transfer_lines.push({
                title: `One-way transfer from ${outbound.from_name} to ${outbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                journey: `One-way transfer from ${outbound.from_name} to ${outbound.to_name}`,
                passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                airport_transfer: is_airport_transfer,
                order_line: transfer,
                vehicle: vehicle?.vehicle
                    ? {
                        id: vehicle?.vehicle?.id,
                        name: vehicle?.vehicle?.name,
                        image: typeof vehicle?.vehicle?.image === "string"
                            ? JSON.parse(vehicle?.vehicle?.image)
                            : vehicle?.vehicle?.image,
                    }
                    : undefined,
            });
        }
        else if (outbound.to_type === "Course" ||
            inbound.from_type === "Course") {
            if (all_golf_same &&
                golf_transfers.length > 0 &&
                config?.where_when_who.type !== "CUSTOM") {
                transfer_lines.push({
                    title: `Roundtrip transfers from ${outbound.from_name} to all ${region?.name?.en} golf courses in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                    journey: `Roundtrip ${(0, pluralize_1.pluralize)(golf_transfers.length, "transfer", "transfers", true)} from ${outbound.from_name} to ${golf_transfers.length > 1
                        ? `all ${region?.name?.en} golf courses`
                        : golf_transfers[0]?.latest_version?.config?.transfer_legs
                            .outbound.to_name}`,
                    passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                    airport_transfer: is_airport_transfer,
                    order_line: transfer,
                    vehicle: vehicle?.vehicle
                        ? {
                            id: vehicle?.vehicle?.id,
                            name: vehicle?.vehicle?.name,
                            image: typeof vehicle?.vehicle?.image === "string"
                                ? JSON.parse(vehicle?.vehicle?.image)
                                : vehicle?.vehicle?.image,
                        }
                        : undefined,
                });
            }
            else {
                if (is_custom) {
                    transfer_lines.push({
                        title: `Custom transfer from ${outbound.from_name} to ${outbound.to_name} to ${inbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                        journey: `Custom transfer from ${outbound.from_name} to ${outbound.to_name} to ${inbound.to_name}`,
                        passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                        airport_transfer: is_airport_transfer,
                        order_line: transfer,
                        vehicle: vehicle?.vehicle
                            ? {
                                id: vehicle?.vehicle?.id,
                                name: vehicle?.vehicle?.name,
                                image: typeof vehicle?.vehicle?.image === "string"
                                    ? JSON.parse(vehicle?.vehicle?.image)
                                    : vehicle?.vehicle?.image,
                            }
                            : undefined,
                    });
                }
                else {
                    transfer_lines.push({
                        title: `Roundtrip transfers from ${outbound.from_name} to ${outbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                        journey: `Roundtrip transfers from ${outbound.from_name} to ${outbound.to_name}`,
                        passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                        airport_transfer: is_airport_transfer,
                        order_line: transfer,
                        vehicle: vehicle?.vehicle
                            ? {
                                id: vehicle?.vehicle?.id,
                                name: vehicle?.vehicle?.name,
                                image: typeof vehicle?.vehicle?.image === "string"
                                    ? JSON.parse(vehicle?.vehicle?.image)
                                    : vehicle?.vehicle?.image,
                            }
                            : undefined,
                    });
                }
            }
        }
    });
    const groupedTransfers = transfer_lines.reduce((acc, transfer) => {
        const key = `${transfer.journey}-${transfer.passengers}-${transfer.vehicle?.id}`;
        if (!acc[key]) {
            acc[key] = {
                ...transfer,
                order_lines: [transfer.order_line],
            };
        }
        else {
            acc[key].order_lines.push(transfer.order_line);
        }
        return acc;
    }, {});
    return Object.values(groupedTransfers);
}
function checkMatchingTransfers(transfers) {
    if (transfers.length === 0)
        return false;
    // Extract the criteria from the first transfer
    const firstTransfer = transfers[0];
    const numberOfPassengers = firstTransfer.where_when_who.number_of_passengers;
    const vehicleId = firstTransfer?.vehicle?.vehicle?.id;
    const fromArea = firstTransfer.transfer_legs.outbound.from_area;
    // Check if all transfers match the criteria
    return transfers.every((transfer) => {
        return (transfer.where_when_who.number_of_passengers === numberOfPassengers &&
            transfer?.vehicle?.vehicle?.id === vehicleId &&
            transfer.transfer_legs.outbound.from_area === fromArea);
    });
}
const getGolfName = (config) => {
    const hasCaddies = () => {
        if (config?.product?.config?.caddie) {
            return true;
        }
        if (config?.caddies?.enabled &&
            config?.caddies?.payment_option !== "ON_ARRIVAL" &&
            config?.caddies?.type === "SINGLE_CADDIE") {
            return true;
        }
        return false;
    };
    const hasSharedCaddies = () => {
        if (config?.product?.config?.shared_caddie) {
            return true;
        }
        if (config?.caddies?.enabled &&
            config?.caddies?.payment_option !== "ON_ARRIVAL" &&
            config?.caddies?.type === "SHARED_CADDIE") {
            return true;
        }
        return false;
    };
    const hasCarts = () => {
        if (config?.product?.config?.single_cart) {
            return true;
        }
        if (config?.carts?.enabled &&
            config?.carts?.payment_option !== "ON_ARRIVAL" &&
            config?.carts?.types
                ?.filter((x) => x.type === "SINGLE_CART")
                ?.find((x) => x.cost_per_cart > 0 && x.quantity > 0)) {
            return true;
        }
        return false;
    };
    const hasSharedCarts = () => {
        if (config?.product?.config?.shared_cart) {
            return true;
        }
        if (config?.carts?.enabled &&
            config?.carts?.payment_option !== "ON_ARRIVAL" &&
            config?.carts?.types
                ?.filter((x) => x.type === "SHARED_CART")
                ?.find((x) => x.cost_per_cart > 0 && x.quantity > 0)) {
            return true;
        }
        return false;
    };
    return `${config?.product?.config?.holes}  Holes ${hasCaddies() ? `+ Caddie` : ``} ${hasSharedCaddies() ? `+ Shared Caddie` : ``}${hasCarts() ? `+ Cart` : ``} ${hasSharedCarts() ? `+ Shared Cart` : ``} ${config?.product?.type !== "Standard" ? `(${config?.product?.type}) ` : ""}· ${(0, pluralize_1.pluralize)(config.where_when_who?.number_of_golfers, "player", "players")}`;
};
